import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { Combobox } from '@headlessui/react';

import { useKeyboard } from '$hooks/events';
import Button from '$partials/ui/button';
import IconClose from '$partials/icons/close';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */
/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const SearchDialog = ({
    closeModal,
    isModalOpen = false,
    keywords = [],
    tags = [],
}) => {
    const dialogRef = useRef();
    const formRef = useRef();
    const inputRef = useRef();
    const [input, setInput] = useState('');
    const [query, setQuery] = useState('');

    const options = keywords.filter((option) =>
        option.title
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''))
    );

    const handleKeyPress = (event) => {
        // enter
        if (event.keyCode === 13) {
            event.preventDefault();
            formRef.current.submit();
        }
    };

    useKeyboard({
        'Escape': () => isModalOpen && closeModal(false)
    }, [isModalOpen]);

    useEffect(() => {
        // use close/showModal methods to trigger browser's toplayer/inert mode
        const dialogNode = dialogRef.current;
        if (isModalOpen) {
            if (dialogNode && !dialogNode.open) {
                dialogNode.showModal?.();
                disableBodyScroll(dialogNode);
            }
        } else {
            if (dialogNode && dialogNode.open) {
                enableBodyScroll(dialogNode);
                dialogNode.close?.();
            }
        }
    }, [isModalOpen]);


    useEffect(() => {
        if (input) {
            formRef.current.submit();
        }
    }, [input]);

    return (
        <dialog
            className='dialog-reset backdrop:bg-transparent'
            ref={dialogRef}>
            <div
                className='w-full fixed top-0 bottom-0 left-0'>
                <div className='bg-grey w-full h-screen md:h-auto md:mt-140'>
                    <div className='layout-container text-left py-40'>
                        {/* Search */}
                        <div className='flex justify-between items-center'>
                            <p className='type-style-3 md:type-style-5 px-10 sm:px-20'>Search</p>
                            <Button
                                className='px-10 sm:px-20'
                                onClick={() => closeModal(false)}
                                renderIcon={<IconClose/>}
                                type='button'/>
                        </div>
                        <form
                            action='/search'
                            className='relative width-100 mt-20 md:mt-40'
                            method='GET'
                            ref={formRef}>
                            <div className='px-10 sm:px-20 relative'>
                                <Combobox
                                    onChange={(val)=>{
                                        setInput(val.title);
                                    }}
                                    value={input}>
                                    <fieldset className='flex bg-white rounded-l'>
                                        <Combobox.Input
                                            className='h-[45px] w-full border-0 rounded-l focus:ring-0 pl-20 py-10 type-style-7 '
                                            displayValue={(topic) => topic.title}
                                            onChange={(event) => {
                                                setQuery(event.target.value);}}
                                            onKeyUp={handleKeyPress}
                                            ref={inputRef}/>
                                        <button
                                            className='inset-y-0 flex items-center bg-orange text-white px-20 rounded '
                                            type='submit'>
                                            Go!
                                        </button>
                                    </fieldset>

                                    <Combobox.Options>
                                        {options.length > 0 && query !== '' &&  (
                                            options.map((option) => (
                                                <Combobox.Option
                                                    className={({ active }) =>
                                                        `relative cursor-pointer select-none py-20 px-20  type-style-7 rounded mt-1 ${
                                                            active ? 'bg-orange text-white' : 'text-orange bg-white'
                                                        }`
                                                    }
                                                    key={option.id}
                                                    value={option}>
                                                    <span className=''>
                                                        {option.title}
                                                    </span>
                                                </Combobox.Option>
                                            ))
                                        )}
                                    </Combobox.Options>
                                </Combobox>
                            </div>
                            <input
                                name='term'
                                type='hidden'
                                value={input || query} />
                        </form>

                        {/* Tags */}
                        {tags.length > 0 && (
                            <div className='px-10 sm:px-20 mt-40'>
                                <p className='type-style-3 md:type-style-5'>Trending</p>
                                <div className='flex mt-10 md:mt-30'>
                                    {tags.map((tag, idx) => (
                                        <Button
                                            as='a'
                                            className='flex items-center bg-white text-orange rounded  type-style-7 px-20 py-15 mr-10 mt-10'
                                            href={`${tag.__typename === 'BvaMagazineSportPage' ? '/activities/' : tag.__typename === 'BvaMagazineTopicPage' ? '/topics/' : ''}${tag.slug}`}
                                            key={`related-tag-${tag?.title}-${idx}`}
                                            type='button'>
                                            {tag?.title}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </dialog>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    closeModal: PropTypes.func.isRequired,
    isModalOpen: PropTypes.bool,
    keywords: PropTypes.array,
    tags: PropTypes.array,
};

SearchDialog.propTypes = propTypes;

export default SearchDialog ;
